import { connect, WindowMessenger, RemoteProxy, Methods } from 'penpal'
import {
    ChildWidgetIframeMethods,
    NewspageEmbedWidgetViewIframeParentMethods,
    PublicationLightboxWidgetViewIframeParentMethods,
    UserTraits,
} from '@noticeable-app/noticeable-common'
import { getMatchingPathPattern, getProjectId, getPublicationIds, isEmbedded } from './utility'
import { registerFigureClickEventListeners } from './lightbox'
import { setupIntersectionObserver } from './publication'

import { WebStorage } from './storage'

export async function embed(): Promise<boolean> {
    if (isEmbedded()) {
        const connection = connect<
            NewspageEmbedWidgetViewIframeParentMethods & PublicationLightboxWidgetViewIframeParentMethods
        >({
            messenger: new WindowMessenger({
                allowedOrigins: ['*'],
                remoteWindow: window.parent,
            }),
            methods: {
                onIdentityChange: onIdentityChange,
                setSdkPreferences: async (preferences) => {
                    window.noticeable['sdkPreferences'] = preferences
                    setupIntersectionObserver()
                },
            } as Partial<ChildWidgetIframeMethods> & Methods,
        })
        const parent = await connection.promise
        window.noticeable['parentWindow'] = parent

        onLoadCompleted(() => {
            registerHeightListener(parent)
        })

        parent.setLocation(iframeUrlPath())

        const matchingPathPattern = getMatchingPathPattern()
        if (matchingPathPattern.startsWith('/publications/')) {
            parent.setPublicationReadSeen(getPublicationIds() ?? [])
        } else if ('/' === matchingPathPattern) {
            if (window.noticeable.contentful) {
                parent.setPublicationReadSeen(getPublicationIds() ?? [])
            } else {
                parent.setPublicationSeen(getPublicationIds() ?? [])
            }
        }

        setupPublicationImagesToOpenInParentLightbox(parent)
        return true
    }

    return false
}

function iframeUrlPath(): string {
    const path = window.location.pathname
    if (window.location.hostname === 'noticeable.news' || window.location.hostname === 'staging.noticeable.news') {
        return '/' + path.substr(path.indexOf('/', 1) + 1)
    }
    return path
}

async function onIdentityChange(userId: string, userSsoToken: string, userTraits: UserTraits) {
    const projectId = getProjectId()
    WebStorage.setUserId(projectId, userId)
    WebStorage.setUserSsoToken(projectId, userSsoToken)
    WebStorage.setUserTraits(projectId, userTraits)
}

function onLoadCompleted(func: Function) {
    if (document.readyState === 'complete') {
        func()
    } else {
        window.addEventListener('load', function () {
            func()
        })
    }
}

function registerHeightListener(
    parent: RemoteProxy<NewspageEmbedWidgetViewIframeParentMethods & PublicationLightboxWidgetViewIframeParentMethods>,
) {
    if (window['ResizeObserver']) {
        const observer = new window['ResizeObserver']((entries) => {
            const container = document.body.querySelector('#global-container')
            const height = container ? container.clientHeight : document.body.clientHeight
            parent.setHeight(height)
        })
        observer.observe(document.body)
    } else {
        const container = document.body.querySelector('#global-container')
        const height = container ? container.clientHeight : document.body.clientHeight
        parent.setHeight(height)
    }
}

function setupPublicationImagesToOpenInParentLightbox(
    parent: RemoteProxy<NewspageEmbedWidgetViewIframeParentMethods & PublicationLightboxWidgetViewIframeParentMethods>,
) {
    registerFigureClickEventListeners((e, lightboxImages, index) => {
        e.preventDefault()
        parent.openLightbox(lightboxImages, index)
    })
}
